import type { CardLayout, ImageType } from '@components/Card2/CardHeader/types';
import { canUseLayout, getImagesRequiresForLayout } from '@components/Card2/CardHeader/utilities';
import { CardFlavours } from '@components/Card2/types';
import { cardEditContext } from '@context/card-edit-context';
import { analytics } from '@modules/analytics';
import { useContext, useEffect, useRef, useState } from 'react';

import type { CardImages, CardProperty } from '.cache/__types__';

interface LayoutPopoverCallbacks {
  onClose: () => void;
  onSave: (selectedLayout: CardLayout, isLocked: boolean) => void;
  onSelectLayout: (layout: CardLayout) => void;
}

function generateWarningMessage(typesMissing: ImageType[]): string {
  const end = 'to access this layout.';

  switch (true) {
    case typesMissing.length === 3:
      return `Add a profile, logo, and cover photo ${end}`;

    case typesMissing.includes('logo') && typesMissing.includes('profile'):
      return `Add a profile picture and logo ${end}`;

    case typesMissing.includes('logo') && typesMissing.includes('cover'):
      return `Add a logo and cover photo ${end}`;

    case typesMissing.includes('cover') && typesMissing.includes('profile'):
      return `Add a profile picture and cover photo ${end}`;

    case typesMissing.includes('cover'):
      return `Add a cover photo ${end}`;

    case typesMissing.includes('logo'):
      return `Add a logo ${end}`;

    case typesMissing.includes('profile'):
      return `Add a profile picture ${end}`;

    default:
      return 'Add an image';
  }
}

export const useEditLayoutPopover = (
  show: boolean,
  { onClose, onSelectLayout, onSave }: LayoutPopoverCallbacks,
  cardImages?: CardImages | null,
  layoutProperty?: CardProperty | null,
) => {
  const [warningMessage, setWarningMessage] = useState<string | undefined>();

  // biome-ignore lint/correctness/useExhaustiveDependencies: historic
  useEffect(() => {
    if (warningMessage) {
      const timeout = setTimeout(() => setWarningMessage(undefined), 2000);
      return () => clearTimeout(timeout);
    }
  }, [warningMessage, setWarningMessage]);

  const [showUnlinkWarning, setShowUnlinkWarning] = useState(false);
  const linked = !!layoutProperty?.is_profile_linked;
  const [fieldsChanged, setFieldsChanged] = useState(false);
  const originalLockStateRef = useRef<{ isLocked: boolean }>(undefined);
  const [isLocked, setIsLocked] = useState(false);
  const [selectedLayout, setSelectedLayout] = useState<CardLayout | undefined>(undefined);
  const { flavour, isCardOwner } = useContext(cardEditContext);

  useEffect(() => {
    setIsLocked(layoutProperty?.value_locked || false);
  }, [layoutProperty?.value_locked]);

  useEffect(() => {
    setSelectedLayout(layoutProperty?.value as CardLayout);
  }, [layoutProperty?.value]);

  // decompose for use when checking we can select a layout
  const { profile, cover, logo } = {
    profile: cardImages?.profile_picture?.edited_url || cardImages?.profile_picture?.value_provider,
    logo: cardImages?.company_logo?.edited_url || cardImages?.company_logo?.value_provider,
    cover: cardImages?.cover_photo?.edited_url || cardImages?.cover_photo?.value_provider,
  };

  useEffect(() => {
    if (show && originalLockStateRef.current === undefined) {
      originalLockStateRef.current = { isLocked };
    } else if (!show) {
      originalLockStateRef.current = undefined;
    }
  }, [show, isLocked]);

  function handleCancel() {
    setFieldsChanged(false);
    onClose();
  }

  function handleClosePopover() {
    onClose();
  }

  function handleSelectLayout(layout: CardLayout) {
    if ((!isLocked || canLock()) && canUseLayout({ cover, logo, profile }, layout)) {
      setFieldsChanged(true);
      setSelectedLayout(layout);
      onSelectLayout(layout);
      return;
    }

    if (isLocked && !canLock()) {
      setWarningMessage('Unlock the layout to change it.');
      return;
    }

    const requiredImages = getImagesRequiresForLayout({ cover, logo, profile }, layout);
    setWarningMessage(generateWarningMessage(requiredImages));
  }

  function performSave() {
    if (!selectedLayout) {
      return;
    }

    analytics.track({ name: 'changelayout_confirmed', properties: { module: 'card_designer' } });
    onSave(selectedLayout, isLocked);
    onClose();
    setFieldsChanged(false);
  }

  function onUnlink() {
    // cardFieldsDispatch({ type: 'UNLINK_ATTRIBUTE', id: 'layout' });
  }

  function handleSave() {
    if (linked) {
      setShowUnlinkWarning(true);
    } else performSave();
  }

  function handleCancelUnlink() {
    setShowUnlinkWarning(false);
    handleClosePopover();
  }

  function handleConfirmUnlink() {
    onUnlink();
    performSave();
  }

  function handleToggleLock() {
    setIsLocked(!isLocked);
    setFieldsChanged(true);
  }

  const canLock = () => {
    if (isCardOwner) {
      return false;
    }

    // Can only lock a template on a Template or team card
    if (flavour === CardFlavours.TEMPLATE || flavour === CardFlavours.TEAM) {
      return true;
    }

    return false;
  };

  return {
    canLock: canLock(),
    fieldsChanged,
    isLocked,
    layout: layoutProperty?.value as CardLayout,
    linked,
    setFieldsChanged,
    showUnlinkWarning,
    warningMessage,
    handlers: {
      handleCancel,
      handleClosePopover,
      handleSave,
      handleToggleLock,
      handleCancelUnlink,
      handleConfirmUnlink,
      handleSelectLayout,
    },
  };
};
